import { CategoryIcon } from '@modules/common/components/CategoryIcon';
import { GetChartLabelOptions } from '@modules/common/types';
import { getFieldNameFromEqlField } from '@modules/common/utils';
import { CooccurrenceIcon } from '@modules/quantify/components/CooccurrenceIcon';
import { useQuantifyStore } from '@modules/quantify/store/quantifyStore';
import { QuantifyReportDataPointBase } from '@modules/quantify/store/types';
import { LabelUpdateInProgressIcon } from '@modules/taxonomy/components/LabelUpdateInProgressIcon';
import { TaxonomyLabelStateAlertMessage } from '@modules/taxonomy/components/TaxonomyLabelStateAlertMessage';
import { getInfoMessageForLabelState } from '@modules/taxonomy/helpers';
import { Stack } from '@mui/material';
import { useTheme } from '@mui/system';
import { LabelType } from 'generated/graphql';
import { capitalize } from 'lodash';
import { useCallback } from 'react';
import { FieldNames } from 'v2/core/entities/fields';

interface useGetChartLabelOptionsParams {
  data: QuantifyReportDataPointBase[];
}

export const useGetChartLabelOptions = ({
  data,
}: useGetChartLabelOptionsParams): GetChartLabelOptions => {
  const theme = useTheme();

  const groupBy = useQuantifyStore(({ query }) => query.groupByField);
  const fieldName = groupBy ? getFieldNameFromEqlField(groupBy) : undefined;
  const labelType =
    fieldName === FieldNames.Reason ? LabelType.Reason : LabelType.Aspect;

  return useCallback(
    ({
      id,
      size,
      hideSecondaryIcon = false,
      showIndentForEmptySecondaryIcon = true,
      shouldHideTooltip = false,
    }: {
      id: string;
      size?: number;
      hideSecondaryIcon?: boolean;
      showIndentForEmptySecondaryIcon?: boolean;
      shouldHideTooltip?: boolean;
    }) => {
      const dataPoint = data.find((dataPoint) => dataPoint.id === id);

      const category = dataPoint?.category;
      const shouldShowCategory = !!category;

      const shouldShowCoOccurringIcon =
        !dataPoint?.isRelevant && !hideSecondaryIcon;
      const coOccurringIconSize = size ? size + 5 : undefined; // +5 to make it the same size as the category icon
      const hasCoOccurringDataPoints = data.some(
        (dataPoint) => !dataPoint.isRelevant,
      );

      const isInvisibleLabel = Boolean(dataPoint?.isInvisible);
      const isUpdateInProgressLabel = Boolean(dataPoint?.isUpdateInProgress);
      const inProgressIconSize = size ? size + 2 : undefined; // +2 to make it the same size as the category icon

      const shouldShowStartIcon =
        (hasCoOccurringDataPoints && !hideSecondaryIcon) || shouldShowCategory;

      return {
        startIcon: shouldShowStartIcon ? (
          <Stack direction="row" alignItems="center" columnGap={0.5}>
            {hasCoOccurringDataPoints && !hideSecondaryIcon && (
              <Stack
                minWidth={
                  !showIndentForEmptySecondaryIcon && !shouldShowCoOccurringIcon
                    ? 0
                    : coOccurringIconSize
                }
              >
                {shouldShowCoOccurringIcon && (
                  <CooccurrenceIcon size={coOccurringIconSize} />
                )}
              </Stack>
            )}
            {shouldShowCategory && (
              <Stack data-role="quantify-category-icon">
                <CategoryIcon
                  category={category}
                  size={size}
                  fill={
                    isInvisibleLabel ? theme.palette.text.disabled : undefined
                  }
                />
              </Stack>
            )}
          </Stack>
        ) : null,
        endIcon: isUpdateInProgressLabel ? (
          <LabelUpdateInProgressIcon
            size={inProgressIconSize}
            labelType={labelType}
            shouldHideTooltip={shouldHideTooltip}
          />
        ) : null,
        label: shouldShowCategory ? capitalize(category) : '',
        isDisabled: isInvisibleLabel,
        messageNode:
          isInvisibleLabel || isUpdateInProgressLabel ? (
            <TaxonomyLabelStateAlertMessage
              variant="tooltip"
              labelType={labelType}
              labelState={isInvisibleLabel ? 'invisible' : 'update_in_progress'}
              typographyVariant="caption"
            />
          ) : null,
        labelTooltipTitle: isInvisibleLabel
          ? getInfoMessageForLabelState({
              labelState: 'invisible',
              labelType,
            })
          : null,
      };
    },
    [data, labelType, theme.palette.text.disabled],
  );
};
